"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const omit_1 = __importDefault(require("lodash/omit"));
const isEmpty_1 = __importDefault(require("lodash/isEmpty"));
const forEach_1 = __importDefault(require("lodash/forEach"));
const indexOf_1 = __importDefault(require("lodash/indexOf"));
const questionnaire_1 = require("@zedoc/questionnaire");
const immutable_1 = require("@zedoc/immutable");
const selectors_1 = require("@zedoc/selectors");
const actions_1 = require("./actions");
const mask_1 = require("./mask");
const reducer = (state = {}, action) => {
    switch (action.type) {
        case actions_1.ACTION_SKIP_ANSWER:
        case actions_1.ACTION_SET_VALUE: {
            if (action.meta) {
                const { name, key, field, ts, source } = action.meta;
                const current = state[name] || {};
                const fullKey = `${key}.${field}`;
                const currentValuesAtKey = (0, selectors_1.createGetAtKey)(key)(current.values);
                const newValuesAtKey = Object.assign(Object.assign({}, currentValuesAtKey), { editedTs: ts, source, sequenceNo: 
                    // NOTE: Do not change sequenceNo if it's already assigned.
                    currentValuesAtKey && currentValuesAtKey.sequenceNo !== undefined
                        ? currentValuesAtKey.sequenceNo
                        : (0, questionnaire_1.getMaxSequenceNo)(current.values) + 1 });
                if (action.type === actions_1.ACTION_SKIP_ANSWER) {
                    if (currentValuesAtKey) {
                        return state;
                    }
                    newValuesAtKey.value = action.payload;
                    newValuesAtKey.whyEmpty = questionnaire_1.NULL_ANSWER__SKIPPED;
                }
                else {
                    newValuesAtKey[field] = action.payload;
                }
                return Object.assign(Object.assign({}, state), { [name]: Object.assign(Object.assign({}, current), { values: (0, immutable_1.setAtKey)(current.values, key, newValuesAtKey), touched: (0, immutable_1.setAtKey)(current.touched, fullKey, true), errors: (0, immutable_1.delAtKey)(current.errors, fullKey, {
                            cascade: true,
                        }) }) });
            }
            return state;
        }
        case actions_1.ACTION_ENTER_FORM_VALUES: {
            if (action.meta) {
                const { name, key = '' } = action.meta;
                const getAtKey = (0, selectors_1.createGetAtKey)(key);
                const current = state[name] || {};
                const values = Object.assign({}, getAtKey(current.values));
                const touched = Object.assign({}, getAtKey(current.touched));
                const errors = Object.assign({}, getAtKey(current.errors));
                (0, forEach_1.default)(action.payload, (value, field) => {
                    values[field] = value;
                    touched[field] = (0, mask_1.createMask)(value);
                    delete errors[field];
                });
                return Object.assign(Object.assign({}, state), { [name]: Object.assign(Object.assign({}, current), { values: (0, immutable_1.setAtKey)(current.values, key, values), touched: (0, immutable_1.setAtKey)(current.touched, key, touched), errors: (0, isEmpty_1.default)(errors)
                            ? (0, immutable_1.delAtKey)(current.errors, key, {
                                cascade: true,
                            })
                            : (0, immutable_1.setAtKey)(current.errors, key, errors) }) });
            }
            return state;
        }
        case actions_1.ACTION_INSERT_ELEMENT: {
            if (action.meta) {
                const { name, key, elementId } = action.meta;
                const current = state[name] || {};
                return Object.assign(Object.assign({}, state), { [name]: Object.assign(Object.assign({}, current), { values: (0, immutable_1.setAtKey)((0, immutable_1.pushAtKey)(current.values, `${key}._elementsOrder`, elementId), `${key}._elements.${elementId}._elements`, action.payload || {}), touched: (0, immutable_1.setAtKey)(current.touched, `${key}._elementsOrder`, true), errors: (0, immutable_1.delAtKey)(current.errors, `${key}._elementsOrder`, {
                            cascade: true,
                        }) }) });
            }
            return state;
        }
        case actions_1.ACTION_REMOVE_ELEMENT: {
            if (action.meta) {
                const { name, key } = action.meta;
                const current = state[name] || {};
                let newValues = current.values;
                const elementId = action.payload;
                // NOTE: There's an important reason why we are not using "cascade" here.
                //       Namely, if there's a default value configured for this question
                //       the cascade would reset the form to it's initial state resulting
                //       in the default value being presented instead of an empty list.
                //       What we really want is to indicate that the list MUST be empty.
                newValues = (0, immutable_1.delAtKey)(newValues, `${key}._elements.${elementId}`);
                newValues = (0, immutable_1.pullAtKey)(newValues, `${key}._elementsOrder`, elementId);
                let newTouched = current.touched;
                newTouched = (0, immutable_1.delAtKey)(newTouched, `${key}._elements.${elementId}`, {
                    cascade: true,
                });
                newTouched = (0, immutable_1.setAtKey)(newTouched, `${key}._elementsOrder`, true);
                let newErrors = current.errors;
                newErrors = (0, immutable_1.delAtKey)(newErrors, `${key}._elements.${elementId}`, {
                    cascade: true,
                });
                // NOTE: We are deleting a potential error that could refer to the
                //       number of elements. This is legit because the number of
                //       elements has just changed.
                newErrors = (0, immutable_1.delAtKey)(newErrors, `${key}._elementsOrder`, {
                    cascade: true,
                });
                return Object.assign(Object.assign({}, state), { [name]: Object.assign(Object.assign({}, state[name]), { values: newValues, touched: newTouched, errors: newErrors }) });
            }
            return state;
        }
        case actions_1.ACTION_MOVE_ELEMENT_UP:
        case actions_1.ACTION_MOVE_ELEMENT_DOWN: {
            if (action.meta) {
                const { name, key } = action.meta;
                const getAtKey = (0, selectors_1.createGetAtKey)(`${key}._elementsOrder`);
                const current = state[name] || {};
                const elementId = action.payload;
                const elementsOrder = getAtKey(current.values);
                let newElementsOrder;
                const index = (0, indexOf_1.default)(elementsOrder, elementId);
                if (action.type === actions_1.ACTION_MOVE_ELEMENT_UP) {
                    if (index >= 1) {
                        newElementsOrder = [...elementsOrder];
                        newElementsOrder[index] = elementsOrder[index - 1];
                        newElementsOrder[index - 1] = elementsOrder[index];
                    }
                }
                else if (action.type === actions_1.ACTION_MOVE_ELEMENT_DOWN) {
                    if (index >= 0 && index < elementsOrder.length - 1) {
                        newElementsOrder = [...elementsOrder];
                        newElementsOrder[index] = elementsOrder[index + 1];
                        newElementsOrder[index + 1] = elementsOrder[index];
                    }
                }
                if (newElementsOrder) {
                    return Object.assign(Object.assign({}, state), { [name]: Object.assign(Object.assign({}, state[name]), { values: (0, immutable_1.setAtKey)(current.values, `${key}._elementsOrder`, newElementsOrder) }) });
                }
            }
            return state;
        }
        case actions_1.ACTION_INITIALIZE: {
            if (action.meta) {
                const { name } = action.meta;
                return (0, immutable_1.setAtKey)(state, name, {
                    values: action.payload,
                });
            }
            return state;
        }
        case actions_1.ACTION_CLEAR: {
            if (action.meta) {
                const { name } = action.meta;
                return (0, immutable_1.delAtKey)(state, name);
            }
            return state;
        }
        case actions_1.ACTION_CLEAR_ALL: {
            return {};
        }
        case actions_1.ACTION_REPLACE_FORM_VALUES: {
            const name = action.meta && action.meta.name;
            if (!name) {
                return state;
            }
            return Object.assign(Object.assign({}, state), { [name]: Object.assign(Object.assign({}, state[name]), { values: action.payload, touched: action.payload
                        ? (0, mask_1.intersectMasks)(state[name] && state[name].touched, (0, mask_1.createMask)(action.payload))
                        : null }) });
        }
        case actions_1.ACTION_REPLACE_FORM_ERRORS: {
            const name = action.meta && action.meta.name;
            if (!name) {
                return state;
            }
            return Object.assign(Object.assign({}, state), { [name]: Object.assign(Object.assign({}, state[name]), { errors: action.payload }) });
        }
        case actions_1.ACTION_SYNC_FORM_VALUES: {
            const { name, skip } = action.meta || {};
            if (skip || !name) {
                return state;
            }
            const formValues = action.payload;
            if (!formValues) {
                return Object.assign(Object.assign({}, state), { [name]: (0, omit_1.default)(Object.assign(Object.assign({}, state[name]), { values: {} }), ['errors', 'touched']) });
            }
            return Object.assign(Object.assign({}, state), { [name]: (0, omit_1.default)(Object.assign(Object.assign({}, state[name]), { values: formValues }), ['errors', 'touched']) });
        }
        default:
            return state;
    }
};
exports.default = reducer;
//# sourceMappingURL=reducer.js.map